import { useCallback, useState } from 'react';

import { Button } from '@opendoor/atoms/button';
import { Popover } from '@opendoor/atoms/popover';
import { cn } from '@opendoor/atoms/utils';
import { IconChevronDown } from '@tabler/icons-react';
import { PopoverCheckboxOptions } from 'components/marketplace/shared/PopoverCheckboxOptions';
import { PopoverRadioOptions } from 'components/marketplace/shared/PopoverRadioOptions';
import {
  MARKETPLACE_POPOVER_SURFACE_CLASS,
  type PopoverTriggerRender,
} from 'components/marketplace/shared/popoverStyles';

const AVAILABILITY_OPTIONS = [
  { value: 'forSale', label: 'For sale' },
  { value: 'availableSoon', label: 'Available soon' },
] as const;

type AvailabilityValue = (typeof AVAILABILITY_OPTIONS)[number]['value'];

export interface AvailabilitySelection {
  forSale: boolean;
  availableSoon: boolean;
}

interface QuickAvailabilityFilterProps {
  /**
   * `radio` keeps the two inventory segments mutually exclusive; `checkbox`
   * lets a shopper combine them, and is only served when the available-soon
   * pool carries no MLS-listed homes.
   */
  mode: 'radio' | 'checkbox';
  availableSoon: boolean;
  includeAvailableSoon?: boolean;
  onChange: (selection: AvailabilitySelection) => void;
}

const exclusiveSelection = (availableSoon: boolean): AvailabilitySelection =>
  availableSoon ? { forSale: false, availableSoon: true } : { forSale: true, availableSoon: false };

/**
 * Checked segments for checkbox mode. `availableSoon` names the exclusive
 * available-soon view and `includeAvailableSoon` the combined view; the
 * default query checks only For sale.
 */
const checkedSegmentsFromFlags = (
  availableSoon: boolean,
  includeAvailableSoon: boolean,
): AvailabilityValue[] => {
  if (includeAvailableSoon) {
    return ['forSale', 'availableSoon'];
  }
  return availableSoon ? ['availableSoon'] : ['forSale'];
};

const selectionLabel = ({ forSale, availableSoon }: AvailabilitySelection): string => {
  if (forSale && availableSoon) {
    return 'For sale & available soon';
  }
  return availableSoon ? 'Available soon' : 'For sale';
};

export const QuickAvailabilityFilter = ({
  mode,
  availableSoon,
  includeAvailableSoon = false,
  onChange,
}: QuickAvailabilityFilterProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const checkedSegments: AvailabilityValue[] =
    mode === 'checkbox'
      ? checkedSegmentsFromFlags(availableSoon, includeAvailableSoon)
      : [availableSoon ? 'availableSoon' : 'forSale'];
  const selection: AvailabilitySelection = {
    forSale: checkedSegments.includes('forSale'),
    availableSoon: checkedSegments.includes('availableSoon'),
  };
  const label = selectionLabel(selection);

  const renderTrigger = useCallback<PopoverTriggerRender>(
    (props) => (
      <Button
        {...props}
        analyticsName="marketplace-inventory-filter"
        aria-label={`Listing availability: ${label}`}
        size="sm"
        variant="secondary"
        className="h-10 shrink-0 px-3"
      >
        {label}
        <IconChevronDown size={16} aria-hidden />
      </Button>
    ),
    [label],
  );

  const handleValueChange = useCallback(
    (value: AvailabilityValue) => {
      onChange(exclusiveSelection(value === 'availableSoon'));
      setIsOpen(false);
    },
    [onChange],
  );

  const handleCheckedChange = useCallback(
    (option: AvailabilityValue, checked: boolean) => {
      const nextChecked = new Set<AvailabilityValue>(checkedSegments);
      if (checked) {
        nextChecked.add(option);
      } else {
        nextChecked.delete(option);
      }
      // The sole remaining checked option renders disabled, so an empty
      // selection is unreachable; ignore it should that guard ever lapse.
      if (nextChecked.size === 0) {
        return;
      }
      onChange({
        forSale: nextChecked.has('forSale'),
        availableSoon: nextChecked.has('availableSoon'),
      });
    },
    [checkedSegments, onChange],
  );

  return (
    <Popover.Root open={isOpen} onOpenChange={setIsOpen}>
      <Popover.Trigger render={renderTrigger} />
      <Popover align="start" size="sm" className={cn('w-52', MARKETPLACE_POPOVER_SURFACE_CLASS)}>
        <Popover.Title className="sr-only">Listing availability</Popover.Title>
        {mode === 'checkbox' ? (
          <PopoverCheckboxOptions
            aria-label="Listing availability"
            analyticsName="marketplace-inventory-filter"
            options={AVAILABILITY_OPTIONS}
            values={checkedSegments}
            onCheckedChange={handleCheckedChange}
          />
        ) : (
          <PopoverRadioOptions
            aria-label="Listing availability"
            analyticsName="marketplace-inventory-filter"
            options={AVAILABILITY_OPTIONS}
            value={selection.availableSoon ? 'availableSoon' : 'forSale'}
            onValueChange={handleValueChange}
          />
        )}
      </Popover>
    </Popover.Root>
  );
};
